import parse from 'html-react-parser';
import { Icon } from 'app/components/BasicIcons/FontAwesome';
import { useEffectOnMount } from 'app/hooks/useEffectOnMount';
import { OptionsObject, SnackbarProvider, useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMessagesSlice } from './slice';
import { selectMessages, selectMessagesAlreadyExist } from './slice/selector';
import {
  CalendarMessageProps,
  LoanDesk,
  Message,
  messageType,
  StartedReservation,
  UsageReservation,
} from './slice/type';
import { useTranslation } from 'react-i18next';
import { translations } from 'locales/translations';
import { Button } from '../BasicButtons/Button';
import { AssetLink } from 'app/pages/AssetPopUp/OpenAssetDetails';
import { Tooltip } from '../BasicTooltips/Tooltip';
import { createStyles, Link, makeStyles, Theme } from '@material-ui/core';
import { AllowedSettings } from 'utils/globalSettings';
import { Box } from '../basic/Wrappers/Box';
import ButtonLink from '../BasicLinks/ButtonLink';
import { useOpenReservationSidePanel } from 'app/pages/ReservationDetails/Details/useOpenReservationSidePanel';
import { H3 } from '../Typography';
import { Entity } from 'types/common';
import { dashboardKey } from '../Dashboard/slice/types';
import { DialogConfirm } from '../DialogConfirm';

const useMessageStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    root: {
      zIndex: theme.zIndex.modal,
    },
    item: {
      backgroundColor: theme.palette.background.default + ' !important',
      color: theme.palette.text.primary + ' !important',
      border: '1px solid ' + theme.palette.info.main,
      minWidth: 'auto',
    },
    base: {
      '& > div': {
        zIndex: theme.zIndex.modal - 1,
      },
      //this accesses the div that can effect the justify content.
      //if notistack is updated, it will likely not be needed as the bug has been fixed in newer versions.
      '&>div>div>div': {
        justifyContent: 'flex-end',
      },
    },
  }),
);
const MaxMessage = 3;
const closeAllKey = 'closeAllMessagesKey';

export type pageNames = dashboardKey | 'calendar' | 'assetDetails';
export const pageToType: Partial<Record<pageNames, messageType[]>> = {
  calendar: [messageType.calendar],
  assetDetails: [messageType.reservationStarted, messageType.usageReservation],
};
export interface MessageProps {
  pageName: pageNames;
  selectedEquipment?: Entity<number>[];
  secondaryPage?: boolean;
}

export const Messages = ({ secondaryPage, ...props }: MessageProps) => {
  const classes = useMessageStyles();
  const alreadyExists = useSelector(selectMessagesAlreadyExist);

  return (
    <span className={classes.base}>
      <SnackbarProvider
        hideIconVariant
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        preventDuplicate
        variant="info"
        maxSnack={MaxMessage + 1}
        classes={{
          root: classes.root,
          variantInfo: classes.item,
        }}
      >
        <MessagesChildren
          {...props}
          secondaryPage={secondaryPage && alreadyExists > 1}
        />
      </SnackbarProvider>
    </span>
  );
};

const MessagesChildren = (props: MessageProps) => {
  const { pageName, selectedEquipment } = props;
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { actions } = useMessagesSlice();
  const allMessages = useSelector(selectMessages);
  const classes = useStyles();
  const messages = useMemo(() => {
    const types = pageToType[props.pageName];
    if (types === undefined) return allMessages;
    return allMessages.filter(m => types.includes(m.type));
  }, [allMessages, props.pageName]);

  useEffectOnMount(() => {
    if (props.secondaryPage) return;
    dispatch(
      actions.init({
        pageName: pageName,
        selectedEquipment: selectedEquipment,
      }),
    );

    return () => {
      dispatch(actions.clear());
    };
  });
  useEffect(() => {
    dispatch(actions.clear());
    closeSnackbar();
    dispatch(
      actions.init({
        pageName: pageName,
        selectedEquipment: selectedEquipment,
      }),
    );
  }, [actions, closeSnackbar, dispatch, pageName, selectedEquipment]);
  useEffect(() => {
    if (props.secondaryPage) {
      closeSnackbar();
      return;
    }
    if (messages.length > 1) {
      const closeAllOptions: OptionsObject = {
        persist: true,
        key: closeAllKey,
      };

      enqueueSnackbar(
        <ButtonLink
          onClick={() => {
            closeSnackbar();
            dispatch(actions.clear());
          }}
        >
          <Icon icon="close" />
          <WithSpace>
            <CloseAll />
          </WithSpace>
        </ButtonLink>,
        closeAllOptions,
      );
    } else {
      closeSnackbar(closeAllKey);
    }
    messages.slice(0, MaxMessage).forEach(m => {
      const close = () => {
        dispatch(actions.remove(m.key));
        closeSnackbar(m.key);
      };
      const options: OptionsObject = {
        persist: true,
        key: m.key,
        action: key => (
          <Box
            width={'100%'}
            display="flex"
            flexDirection="column-reverse"
            alignItems="center"
            justifyContent="space-between"
          >
            {/* {m.type === messageType.removableAnnouncment && (
              <GotIt
                onClick={() => {
                  dispatch(actions.remove(key));
                  closeSnackbar(key);
                  dispatch(
                    actions.gotIt(
                      AllowedSettings[AllowedSettings.DashboardAnnouncement],
                    ),
                  );
                }}
              />
            )} */}
            <Icon icon="close" onClick={close} />
          </Box>
        ),
      };

      enqueueSnackbar(
        <>
          <span className={classes.root}>
            <RenderMessge message={m} close={close} />
          </span>
          <>
            {m.type === messageType.removableAnnouncment && (
              <GotIt
                onClick={() => {
                  dispatch(actions.remove(m.key));
                  closeSnackbar(m.key);
                  dispatch(
                    actions.gotIt(
                      AllowedSettings[AllowedSettings.DashboardAnnouncement],
                    ),
                  );
                }}
              />
            )}
          </>
        </>,
        options,
      );
    });
  }, [
    actions,
    classes,
    closeSnackbar,
    dispatch,
    enqueueSnackbar,
    messages,
    props.secondaryPage,
  ]);
  return null;
};

const CloseAll = () => {
  const { t } = useTranslation();
  return <>{t(translations.CloseAll)}</>;
};

const GotIt = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <Button size="small" onClick={onClick}>
      {t(translations.GotIt)}
    </Button>
  );
};

const RenderMessge = ({
  message,
  close,
}: withClose<{
  message: Message;
}>) => {
  switch (message.type) {
    case messageType.Announcment:
    case messageType.removableAnnouncment:
      return <Truncicated title={null} value={message.props as string} />;
    case messageType.reservationStarted:
      const props = message.props as StartedReservation;
      return <ReservationStartedMessage close={close} {...props} />;
    case messageType.usageReservation:
      return (
        <UsageReservationMessage
          close={close}
          {...(message.props as UsageReservation)}
        />
      );
    case messageType.loadDesk:
      return <LoanDeskMessage {...(message.props as LoanDesk)} />;
    case messageType.calendar:
      return <CalendarMessage {...(message.props as CalendarMessageProps)} />;
    default:
      return null;
  }
};

type withClose<T> = T & { close: () => void };

const ReservationStartedMessage = ({
  close,
  ...props
}: withClose<StartedReservation>) => {
  const { t } = useTranslation();
  const openSidePanel = useOpenReservationSidePanel();
  const assets = (
    <React.Fragment key="asset_space_link">
      <AssetLink id={props.Asset.Id}>{props.Asset.Name}</AssetLink>
    </React.Fragment>
  );
  if (props.AllowUsersToShortenReservations && props.AllUsagesEnded) {
    return (
      <>
        {assets} {t(translations.InstrumentStillReservered)}
        <Button
          onClick={() => {
            openSidePanel({ id: '' + props.Id });
            close();
          }}
        >
          {t(translations.ReleaseUnusedReservation)}
        </Button>
      </>
    );
  }
  let messages: React.ReactNode = '';
  if (props.AllowUsersToShortenReservations) {
    const message = t(translations.YouHaveOngoingReservations);
    messages = message
      .split('{0}')
      .map<React.ReactNode>((f, i) => (
        <React.Fragment>
          <span key={'ReservationStarted' + i}> {f} </span>
        </React.Fragment>
      ))
      .reduce((prev, curr) => [prev, assets, curr]);
  } else if (props.AllowExtendReservation || props.AllowModification) {
    const message = t(translations.ReservationStarted);
    const link = (
      <WithSpace key="res_space_link">
        <Link
          key="res_link"
          onClick={() => openSidePanel({ id: '' + props.Id })}
        >
          {props.Id}
        </Link>
      </WithSpace>
    );
    messages = message
      .split('{0}')
      .map<React.ReactNode>((m, idx) => (
        <span key={'assets_' + idx}>
          {' '}
          {m
            .split('{1}')
            .map<React.ReactNode>((f, i) => (
              <React.Fragment>
                <span key={'ReservationStarted' + i}> {f} </span>
              </React.Fragment>
            ))
            .reduce((prev, curr) => [prev, assets, curr])}
        </span>
      ))
      .reduce((prev, curr) => [prev, link, curr]);
  }
  const canEdit =
    props.AllowUsersToShortenReservations ||
    props.AllowModification ||
    props.AllowExtendReservation;
  return (
    <>
      {messages}
      {canEdit && (
        <Button
          onClick={() => {
            openSidePanel({ id: '' + props.Id });
            close();
          }}
        >
          {t(translations.UpdateOngoingReservations)}
        </Button>
      )}
    </>
  );
};

const UsageReservationMessage = ({
  close,
  ...props
}: withClose<UsageReservation>) => {
  const { t } = useTranslation();
  const assets = (
    <React.Fragment key="asset_space_link">
      <AssetLink id={props.Id}>{props.Name}</AssetLink>
    </React.Fragment>
  );
  const message = t(translations.IpRelay_reservationTimeForInstrument);
  const messages = message
    .split('{0}')
    .map<React.ReactNode>((f, i) => (
      <span key={'UsageReservation' + i}> {f} </span>
    ))
    .reduce((prev, curr) => [prev, assets, curr]);
  return (
    <>
      {messages}
      <Button
        target="_blank"
        href={{ path: '/Usage.aspx', search: { id: props.Id } }}
        onClick={close}
      >
        {t(translations.IpRelay_LinkToUsage)}
      </Button>
    </>
  );
};

const CalendarMessage = (props: CalendarMessageProps) => {
  return (
    <Truncicated value={props.Body ?? ''} title={props.Title}>
      {props.Body && parse(props.Body)}
    </Truncicated>
  );
};

const LoanDeskMessage = (props: LoanDesk) => {
  const { t } = useTranslation();
  const openSidePanel = useOpenReservationSidePanel();
  const message = t(translations.ReservationStartedMessage_LoanDesk);
  const link = (
    <WithSpace>
      <Link onClick={() => openSidePanel({ id: '' + props.Id })}>
        {props.Id}
      </Link>
    </WithSpace>
  );
  const asset = (
    <WithSpace>
      <AssetLink id={props.AssetDetails.Id}>
        {props.AssetDetails.Name}
      </AssetLink>
    </WithSpace>
  );
  const messages = message
    .split('{0}')
    .map(m =>
      m
        .split('{1}')
        .map<React.ReactNode>((f, i) => (
          <React.Fragment key={'LoanDesk' + i}> {f} </React.Fragment>
        ))
        .reduce((prev, curr) => [prev, asset, curr]),
    )
    .reduce((prev, curr) => [prev, link, curr]);
  return <>{messages}</>;
};

const WithSpace = ({ children }: React.PropsWithChildren<{}>) => {
  return (
    <>
      &nbsp;
      {children}
      &nbsp;
    </>
  );
};

const Truncicated = ({
  value,
  title,
  children,
}: React.PropsWithChildren<{ value: string; title: string | null }>) => {
  const classes = useStyles();
  const height = useMemo(() => getSize(value, title, classes.root), [
    classes.root,
    title,
    value,
  ]);
  const parsed = parse(value);
  const moreTitle = useMemo(() => MoreTitle(title), [title]);
  if (height > 140) {
    return (
      <Tooltip title={<>{parsed}</>}>
        <>
          <span className={classes.truncate}>
            <MorePopUp Link={moreTitle}>
              <TitleIfExists title={title} />
              {children ?? parsed}
            </MorePopUp>
            {children ?? parsed}
          </span>
          <MoreLink>
            <TitleIfExists title={title} />
            {children ?? parsed}
          </MoreLink>
        </>
      </Tooltip>
    );
  }
  return (
    <span>
      <TitleIfExists title={title} />
      {children ?? parsed}
    </span>
  );
};

const getSize = (value: string, title: string | null, style: string) => {
  const div = document.createElement('div');
  div.classList.add(style);
  if (title !== null) {
    const h3 = document.createElement('h3');
    h3.innerText = title;
    div.appendChild(h3);
  }
  const inner = document.createElement('div');
  inner.innerHTML = value;
  div.appendChild(inner);
  document.body.appendChild(div);
  const height = div.clientHeight;
  div.parentNode?.removeChild(div);
  return height;
};

const MoreLink = ({ children }: React.PropsWithChildren<{}>) => {
  return <MorePopUp Link={MoreButton}>{children}</MorePopUp>;
};

const TitleIfExists = ({ title }: { title: string | null }) => {
  return (title ?? '') === '' ? null : <H3>{title}</H3>;
};

const MoreTitle = (title: string | null): MorePopUpLink => {
  return ({ onClick }) => {
    return (title ?? '') === '' ? null : (
      <H3 onClick={onClick} style={{ cursor: 'pointer' }}>
        {title}
      </H3>
    );
  };
};

const MoreButton: MorePopUpLink = ({ onClick }) => {
  return (
    <Button onClick={onClick} variant="text" size="small">
      More
    </Button>
  );
};

type MorePopUpLink = React.FC<{ onClick: () => void }>;

const MorePopUp = ({
  Link,
  children,
}: React.PropsWithChildren<{
  Link: MorePopUpLink;
}>) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const { t } = useTranslation();
  return (
    <>
      <Link onClick={() => setOpen(true)} />
      <DialogConfirm
        isOpen={open}
        onClose={close}
        body={children}
        confirmButtonLabel={t(translations.OK)}
        onConfirm={close}
      />
    </>
  );
};

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      maxWidth: '380px',
      maxHeight: '180px',
      zIndex: theme.zIndex.drawer,
    },
    truncate: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      height: '150px',
      display: '-webkit-box',
      WebkitLineClamp: 5,
      WebkitBoxOrient: 'vertical',
    },
  }),
);
